import React from 'react';

import { useCorrespondencePackageRetrieveQuery } from '@/services/api/correspondenceService';

import { useCorrespondenceUI } from '../contexts/CorrespondenceUIContext';
import { usePackageComponents } from '../hooks';
import { EmptyPreviewState } from './EmptyPreviewState';
import { PackagePreviewItem, UrlPdf } from './PackagePreviewItem';

/**
 * Preview pane - just renders components in a column
 */
export const PackagePreviewPane: React.FC = () => {
  const { matterId, packageId, selectedComponentId, setSelectedComponentId } =
    useCorrespondenceUI();
  const { components } = usePackageComponents({ packageId, skip: !packageId });

  // Get package-level status for component inheritance
  const { packageStatus, renderedPdfUrl, isMutable } = useCorrespondencePackageRetrieveQuery(
    { matterId, id: packageId || '' },
    {
      skip: !packageId,
      selectFromResult: ({ data }) => ({
        packageStatus: data?.status,
        renderedPdfUrl: data?.rendered_pdf_url,
        isMutable: data?.is_mutable ?? true,
      }),
    },
  );

  const showRenderedPdfFallback =
    (!components || components.length === 0) &&
    Boolean(renderedPdfUrl) &&
    !isMutable;

  if (showRenderedPdfFallback && renderedPdfUrl) {
    return (
      <div className="flex flex-col gap-6 px-4 py-3">
        <UrlPdf url={renderedPdfUrl} />
      </div>
    );
  }

  if (!components || components.length === 0) {
    return <EmptyPreviewState />;
  }

  return (
    <div className="flex flex-col gap-6 px-4 py-3">
      {components.map((component, index) => (
        <PackagePreviewItem
          key={component.id}
          component={component}
          index={index}
          isSelected={component.id === selectedComponentId}
          matterId={matterId}
          onClick={() => setSelectedComponentId(component.id)}
          packageStatus={packageStatus}
        />
      ))}
    </div>
  );
};
